import { useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useStoreState } from "easy-peasy";
import Login from "./Login/LoginContainer";
import Dashboard from "./Dashboard/DashboardContainer";
import NotifierTheme from "./Common/NotifierTheme";
import Notifier from "./Common/Notifier";
import { SnackbarProvider } from "notistack";
import Loading from "./Common/Loading";
import NotifyClose from "./Common/NotifierClose";
import ErrorBoundary from "./Common/ErrorBoundary";
import config from "devextreme/core/config";
import "devextreme/dist/css/dx.common.css";
import "devextreme/dist/css/dx.material.blue.dark.css";
import "../style.css";
import Install from "./Common/Install";

config({
  licenseKey: import.meta.env.VITE_DEVEXTREME_LICENSE_KEY
});

export default function App() {
  const authenticated = useStoreState(state => state.auth.authenticated);

  // Comprehensive user environment detection
  const userEnvironment = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    // Detect OS
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;

    // Detect Browser
    const isChrome = /chrome|crios/.test(userAgent) && !/edg/.test(userAgent);
    const isSafari =
      /safari/.test(userAgent) && !/chrome|crios/.test(userAgent);
    const isEdge = /edg/.test(userAgent);
    const isFirefox = /firefox/.test(userAgent);

    // Detect App Type
    let appType = "browser";

    // Check if running in Electron
    if (window.electronAPI || window.require) {
      appType = "electron";
    }
    // Check if running as PWA
    else if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.navigator.standalone === true
    ) {
      appType = "pwa";
    }

    // Determine platform combination for install instructions
    let platform = "desktop";
    if (isMobile) {
      if (isIOS && isSafari) platform = "ios-safari";
      else if (isIOS && isChrome) platform = "ios-chrome";
      else if (isAndroid && isChrome) platform = "android-chrome";
      else platform = "mobile-other";
    }

    return {
      os: isIOS ? "ios" : isAndroid ? "android" : "desktop",
      browser: isChrome
        ? "chrome"
        : isSafari
        ? "safari"
        : isEdge
        ? "edge"
        : isFirefox
        ? "firefox"
        : "other",
      appType,
      isMobile,
      platform
    };
  }, []);

  // Only enforce PWA on mobile devices
  const shouldShowInstall =
    userEnvironment.isMobile && userEnvironment.appType !== "pwa";

  const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

  const router = sentryCreateBrowserRouter([
    {
      path: "/",
      element: authenticated ? (
        <Navigate to="/dashboard" replace={true} />
      ) : (
        <Navigate to="/login" replace={true} />
      ),
      errorElement: <Navigate to="/error" replace={true} />
    },
    {
      path: "/login",
      element: authenticated ? (
        <Navigate to="/dashboard" replace={true} />
      ) : (
        <Login />
      ),
      errorElement: <Navigate to="/error" replace={true} />
    },
    {
      path: "/dashboard",
      element: authenticated ? (
        <Dashboard />
      ) : (
        <Navigate to="/login" replace={true} />
      ),
      errorElement: <Navigate to="/error" replace={true} />
    },
    {
      path: "/error",
      element: <ErrorBoundary />
    }
  ]);

  return (
    <SnackbarProvider
      Components={{
        default: NotifierTheme,
        success: NotifierTheme,
        error: NotifierTheme,
        warning: NotifierTheme,
        info: NotifierTheme
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right"
      }}
      autoHideDuration={4000}
      action={key => <NotifyClose notificationKey={key} />}
    >
      {shouldShowInstall ? (
        <Install platform={userEnvironment.platform} />
      ) : (
        <>
          <RouterProvider router={router} />
          <Notifier />
          <Loading />
        </>
      )}
    </SnackbarProvider>
  );
}
